
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BackgroundImage } from "@/components/home/BackgroundImage";
import { useBackgroundImage } from "@/hooks/useBackgroundImage";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { RegisterForm } from "./RegisterForm";
import { SignInForm } from "./SignInForm";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Background images from the index page
const BACKGROUND_IMAGES = [
  "https://ehskmwgoablwyqevwegt.supabase.co/storage/v1/object/public/images/Stock_Images/dancing_women_2.jpg",
  "https://ehskmwgoablwyqevwegt.supabase.co/storage/v1/object/public/images/Stock_Images/dancing_women.jpg",
  "https://ehskmwgoablwyqevwegt.supabase.co/storage/v1/object/public/images/Stock_Images/festival_girl_2.jpg",
  "https://ehskmwgoablwyqevwegt.supabase.co/storage/v1/object/public/images/Stock_Images/Nightclub6.jpg",
  "https://ehskmwgoablwyqevwegt.supabase.co/storage/v1/object/public/images/Stock_Images/Nightclub3.jpg"
];

export const AuthPage = () => {
  const { backgroundImage, isLoadingImage } = useBackgroundImage(BACKGROUND_IMAGES);
  const navigate = useNavigate();
  const { user, isLoading, navigateToDashboard } = useAuth();
  
  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (user && !isLoading) {
      console.log("User already authenticated in AuthPage, redirecting to dashboard");
      navigateToDashboard();
    }
  }, [user, isLoading, navigateToDashboard]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden relative">
      {/* Background image with overlay effect */}
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/70 via-black/50 to-black/80 pointer-events-none"></div>
      <BackgroundImage 
        backgroundImage={backgroundImage}
        isLoadingImage={isLoadingImage}
      />
      
      <div className="relative z-20 min-h-screen flex flex-col">
        {/* Back button */}
        <div className="absolute top-4 left-4 z-50">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="text-white/70 hover:text-white hover:-translate-x-1 transition-all"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Back to Home</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>
        
        {/* Auth content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md animate-fade-in">
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6 p-1 bg-black/40 backdrop-blur-sm border border-gray-800/50 rounded-lg shadow-lg">
                <TabsTrigger 
                  value="signin" 
                  className="flex items-center gap-1 transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600/80 data-[state=active]:to-pink-600/80 data-[state=active]:text-white text-xs sm:text-sm"
                >
                  <LogIn className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Sign In</span>
                  <span className="sm:hidden">Login</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="signup" 
                  className="flex items-center gap-1 transition-all data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600/80 data-[state=active]:to-pink-600/80 data-[state=active]:text-white text-xs sm:text-sm"
                >
                  <UserPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Sign Up</span>
                  <span className="sm:hidden">Register</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="signin" className="animate-fade-in transition-all duration-300">
                <div className="glass-card rounded-xl p-6 sm:p-8 border border-border/20 shadow-2xl backdrop-blur-xl bg-card/40">
                  <div className="text-center mb-6 space-y-2">
                    <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
                      Welcome Back
                    </h2>
                    <p className="text-muted-foreground">
                      Sign in to your Noche Loco account
                    </p>
                  </div>
                  <SignInForm />
                </div>
              </TabsContent>

              <TabsContent value="signup" className="animate-fade-in transition-all duration-300">
                <div className="glass-card rounded-xl p-6 sm:p-8 border border-border/20 shadow-2xl backdrop-blur-xl bg-card/40">
                  <div className="text-center mb-6 space-y-2">
                    <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
                      Join Noche Loco
                    </h2>
                    <p className="text-muted-foreground">
                      Create your account and start exploring nightlife
                    </p>
                  </div>
                  <RegisterForm />
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
